import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../../ui';
import { BellIcon, EnvelopeIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import type { NotificationPreferencesFormData } from '../../../../types';
import { defaultNotificationPreferences } from '../../../../stores';

interface NotificationsStepProps {
  initialData: NotificationPreferencesFormData | null;
  onSubmit: (data: NotificationPreferencesFormData) => void;
}

function ToggleSwitch({
  enabled,
  onChange,
  label,
  description,
}: {
  enabled: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description?: string;
}) {
  return (
    <div className="flex items-center justify-between py-3 gap-4">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-surface-800 dark:text-white">{label}</p>
        {description && (
          <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange(!enabled)}
        className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors ${
          enabled ? 'bg-teal-500' : 'bg-surface-300 dark:bg-surface-600'
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            enabled ? 'translate-x-5' : ''
          }`}
        />
      </button>
    </div>
  );
}

function TimePicker({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm text-surface-600 dark:text-surface-400 whitespace-nowrap">
        {label}
      </span>
      <input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-4 py-2.5 rounded-lg border-2 border-surface-200 dark:border-surface-600 bg-white dark:bg-surface-700 text-surface-800 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors cursor-pointer text-base font-medium"
      />
    </div>
  );
}

function ReminderWithTime({
  enabled,
  onToggle,
  time,
  onTimeChange,
  label,
  description,
  timeLabel,
}: {
  enabled: boolean;
  onToggle: (v: boolean) => void;
  time: string;
  onTimeChange: (v: string) => void;
  label: string;
  description: string;
  timeLabel: string;
}) {
  return (
    <div className="py-3">
      {/* Toggle row */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-surface-800 dark:text-white">{label}</p>
          <p className="text-xs text-surface-500 dark:text-surface-400 mt-0.5">{description}</p>
        </div>
        <button
          type="button"
          onClick={() => onToggle(!enabled)}
          className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors ${
            enabled ? 'bg-teal-500' : 'bg-surface-300 dark:bg-surface-600'
          }`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
              enabled ? 'translate-x-5' : ''
            }`}
          />
        </button>
      </div>

      {/* Time picker row - below toggle when enabled */}
      {enabled && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="mt-3 pl-0 sm:pl-4"
        >
          <TimePicker value={time} onChange={onTimeChange} label={timeLabel} />
        </motion.div>
      )}
    </div>
  );
}

export function NotificationsStep({ initialData, onSubmit }: NotificationsStepProps) {
  const [prefs, setPrefs] = useState<NotificationPreferencesFormData>(
    initialData || defaultNotificationPreferences
  );

  const update = (key: keyof NotificationPreferencesFormData, value: boolean | string) => {
    setPrefs((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div>
      <motion.div
        className="text-center mb-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        <p className="text-surface-600 dark:text-surface-400">
          Set up reminders to stay on track. You can change these anytime in settings.
        </p>
      </motion.div>

      <motion.div
        className="space-y-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        {/* Daily Reminders */}
        <div className="bg-white dark:bg-surface-800 rounded-xl p-5 border border-surface-200 dark:border-surface-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-accent-100 dark:bg-accent-900/30">
              <BellIcon className="w-5 h-5 text-accent-600 dark:text-accent-400" />
            </div>
            <h3 className="font-semibold text-surface-800 dark:text-white">Daily Reminders</h3>
          </div>

          <div className="divide-y divide-surface-100 dark:divide-surface-700">
            <ReminderWithTime
              enabled={prefs.checkInReminderEnabled}
              onToggle={(v) => update('checkInReminderEnabled', v)}
              time={prefs.checkInReminderTime}
              onTimeChange={(v) => update('checkInReminderTime', v)}
              label="Morning Check-in"
              description="Start your day with intention"
              timeLabel="Remind at"
            />

            <ReminderWithTime
              enabled={prefs.nightlyReviewReminderEnabled}
              onToggle={(v) => update('nightlyReviewReminderEnabled', v)}
              time={prefs.nightlyReviewReminderTime}
              onTimeChange={(v) => update('nightlyReviewReminderTime', v)}
              label="Nightly Review"
              description="Reflect on your day"
              timeLabel="Remind at"
            />
          </div>
        </div>

        {/* Push Notifications */}
        <div className="bg-white dark:bg-surface-800 rounded-xl p-5 border border-surface-200 dark:border-surface-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30">
              <DevicePhoneMobileIcon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <h3 className="font-semibold text-surface-800 dark:text-white">Push Notifications</h3>
          </div>

          <div className="divide-y divide-surface-100 dark:divide-surface-700">
            <ToggleSwitch
              enabled={prefs.pushMilestones}
              onChange={(v) => update('pushMilestones', v)}
              label="Milestone celebrations"
            />
            <ToggleSwitch
              enabled={prefs.pushSponsorMessages}
              onChange={(v) => update('pushSponsorMessages', v)}
              label="Sponsor messages"
            />
            <ToggleSwitch
              enabled={prefs.pushDailyReflection}
              onChange={(v) => update('pushDailyReflection', v)}
              label="Daily reflection"
            />
            <ToggleSwitch
              enabled={prefs.pushMeetings}
              onChange={(v) => update('pushMeetings', v)}
              label="Meeting reminders"
            />
          </div>
        </div>

        {/* Email Notifications */}
        <div className="bg-white dark:bg-surface-800 rounded-xl p-5 border border-surface-200 dark:border-surface-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-surface-100 dark:bg-surface-700">
              <EnvelopeIcon className="w-5 h-5 text-surface-600 dark:text-surface-400" />
            </div>
            <h3 className="font-semibold text-surface-800 dark:text-white">Email Notifications</h3>
          </div>

          <div className="divide-y divide-surface-100 dark:divide-surface-700">
            <ToggleSwitch
              enabled={prefs.emailWeeklyDigest}
              onChange={(v) => update('emailWeeklyDigest', v)}
              label="Weekly progress digest"
            />
            <ToggleSwitch
              enabled={prefs.emailSponsorUpdates}
              onChange={(v) => update('emailSponsorUpdates', v)}
              label="Sponsor activity updates"
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        className="pt-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Button onClick={() => onSubmit(prefs)} size="lg" className="w-full">
          Continue
        </Button>
      </motion.div>
    </div>
  );
}
